import { Consent, ServiceRequest } from '@medplum/fhirtypes'
import {
  CONSENT_ACTION_CODE_SYSTEM,
  CONSENT_CARE_HOURS_EXTENSION_URL,
  CONSENT_CHARTING_CATEGORY_EXTENSION_URL,
  ConsentActionCode,
  ConsentActorRoleCode,
  ConsentCategoryCode,
  ConsentChartingCategory,
  ConsentStatus
} from 'fhir/Consent/constants'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import { SERVICE_REQUEST_REQUESTED_CARE_HOURS_EXTENSION } from 'fhir/ServiceRequest/constants'
import { findExtension } from 'fhir/utils'
import { BaseConsentFragment, BaseCoverageFragment, ConsentProvisionActor } from 'generated/graphql'
import { isTimePeriodInEffect } from 'utils/dates'
import { checkFeatureConfigByServiceTypes } from 'utils/featureConfig'
import { CustomFeatureConfigKey } from 'utils/featureConfig/constants'

export const getProvisionAuthor = (
  consent: BaseConsentFragment
): Partial<ConsentProvisionActor> | null => {
  const performer = consent.provision?.actor?.find(
    (actor) => actor?.role?.coding?.[0].code === ConsentActorRoleCode.AUTHOR
  )

  return performer ?? null
}

const consentTypeHierarchy: string[] = [ConsentCategoryCode.HOME_HEALTH, '']

export const getPrimaryConsentCategoryCode = (consent: BaseConsentFragment): string | null => {
  const categories = consent.category ?? []

  const sorted = [...categories].sort(
    (a, b) =>
      consentTypeHierarchy.indexOf(a.coding?.find((code) => !!code)?.code ?? '') -
      consentTypeHierarchy.indexOf(b.coding?.find((code) => !!code)?.code ?? '')
  )

  const primaryCategory = sorted.find((val) => !!val)
  return primaryCategory?.coding?.at(0)?.code ?? null
}

export const getCoverageIssuedByPerformer = (
  consent: BaseConsentFragment,
  coverageList: BaseCoverageFragment[]
): BaseCoverageFragment | undefined => {
  const coverageIssuedByPerformer = coverageList.find((coverage) =>
    coverage?.payor?.some((payor) =>
      consent.performer?.some((perf) => {
        return !!perf.reference && !!payor.reference && perf.reference === payor.reference
      })
    )
  )

  return coverageIssuedByPerformer
}

export interface DisplayableAuthorizationHours {
  hours: number
  unit: 'daily' | 'weekly'
}

export const getDisplayableAuthorizationHours = (
  authorizationOrRequest: Consent | BaseConsentFragment | ServiceRequest | null | undefined,
  serviceTypes: ServiceTypeName[]
): DisplayableAuthorizationHours | undefined => {
  const isDailyCareHoursConstraint = checkFeatureConfigByServiceTypes(
    CustomFeatureConfigKey.DailyCareHoursConstraint,
    serviceTypes
  )
  const isWeeklyCareHoursConstraint = checkFeatureConfigByServiceTypes(
    CustomFeatureConfigKey.WeeklyCareHoursConstraint,
    serviceTypes
  )
  if (!authorizationOrRequest) return undefined

  let averageCareHours: number | null
  if (authorizationOrRequest.resourceType === 'Consent') {
    const authorizationConsent = authorizationOrRequest as BaseConsentFragment | Consent
    if ('careHours' in authorizationConsent) {
      const authorizationFragment = authorizationConsent as BaseConsentFragment
      averageCareHours = authorizationFragment.careHours?.at(0)?.valueDecimal ?? null
    } else {
      const fhirAuthorizationConsent = authorizationConsent as Consent
      const careHoursExtension = findExtension(
        fhirAuthorizationConsent.extension ?? [],
        CONSENT_CARE_HOURS_EXTENSION_URL
      )
      averageCareHours = careHoursExtension?.valueDecimal ?? null
    }
  } else {
    const authorizationRequest = authorizationOrRequest as ServiceRequest
    const authorizedUnitsRuleExtension = findExtension(
      authorizationRequest.extension ?? [],
      SERVICE_REQUEST_REQUESTED_CARE_HOURS_EXTENSION
    )
    averageCareHours = authorizedUnitsRuleExtension?.valueDecimal ?? null
  }
  if (averageCareHours === null) return undefined

  if (isDailyCareHoursConstraint) {
    return {
      hours: averageCareHours,
      unit: 'daily'
    }
  } else if (isWeeklyCareHoursConstraint) {
    return {
      hours: averageCareHours,
      unit: 'weekly'
    }
  }
}

export const getAuthorizationChartingCategory = (
  authorizationConsent: BaseConsentFragment | null | undefined
): ConsentChartingCategory | null => {
  if (!authorizationConsent) return null

  return (
    (authorizationConsent.extension?.find(
      (extension) => extension.url === CONSENT_CHARTING_CATEGORY_EXTENSION_URL
    )?.valueString as ConsentChartingCategory) ?? null
  )
}

export const getAuthorizationStatusFromProvisionRule = (
  consentResource: BaseConsentFragment,
  timezone?: string
): ConsentStatus => {
  const consentStatus = consentResource.status
  const nestedProvisionRule = consentResource.provision?.nestedProvision
  const { start, end } = nestedProvisionRule?.period ?? {}
  const isDischargeAction = nestedProvisionRule?.action?.find((action) =>
    action.coding?.find(
      (coding) =>
        coding.system === CONSENT_ACTION_CODE_SYSTEM && coding.code === ConsentActionCode.DISCHARGE
    )
  )
  const isCurrentlyActive = isTimePeriodInEffect(timezone, start, end)

  return isDischargeAction && isCurrentlyActive
    ? ConsentStatus.INACTIVE
    : (consentStatus as ConsentStatus)
}
